import { ToastrService } from 'ngx-toastr';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';

import { HttpClient, HttpEvent, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListResponse, ISnFeedQueryParameters, ISnListQueryParameters } from '@lib/api';
import { SnTip, SnIncident, SnRecording, FsSecretCredentials } from '@lib/api/entities';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

import { ISnRecordingListQueryParameters } from './recordings.service';

@Injectable({ providedIn: 'root' })
export class SnIncidentsAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public sendRequestMedia(id: string, data: SnTip[]): Observable<SnTip[]> {
    return this._httpClient.post(`/api/vaults/${id}/request_media/bulk/`, data).pipe(
      map((response: any[]) => response.map((item) => new SnTip(item))),
      tap({
        next: () => this._toastrService.success(t('vault.incidents.service.requestMediaSent')),
        error: () =>
          this._toastrService.error(t('vault.incidents.service.couldNotCreateRequestMedia')),
      }),
    );
  }

  public sendRequestImage(id: string, data: any): Observable<any> {
    return this._httpClient.post(`/api/vaults/${id}/media/`, data).pipe(
      tap({
        next: () => this._toastrService.success(t('vault.incidents.service.imageSentSuccessfully')),
        error: () => this._toastrService.error(t('vault.incidents.service.unableToSendImage')),
      }),
    );
  }

  public getVaultIncidentList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnIncident>> {
    return this._httpClient.get('/api/vaults/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((incident) => new SnIncident(incident)),
      })),
    );
  }

  public getVaultIncident(id: string): Observable<SnIncident> {
    return this._httpClient
      .get(`/api/vaults/${id}/`)
      .pipe(map((response) => new SnIncident(response)));
  }

  public feedVaultIncidents(params?: ISnFeedQueryParameters): Observable<SnIncident[]> {
    return this._httpClient
      .get('/api/vaults/feed/', { params })
      .pipe(map((response: any) => response.map((incident) => new SnIncident(incident))));
  }

  public getIncidentTipList(
    id: string,
    params?: ISnRecordingListQueryParameters,
  ): Observable<ISnListResponse<SnTip>> {
    return this._httpClient.get(`/api/vaults/${id}/tips/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((tip) => new SnTip(tip)),
      })),
    );
  }

  public unlinkTip(incidentId: string, tipId: string): Observable<null> {
    return this._httpClient.post<null>(`/api/vaults/${incidentId}/tips/${tipId}/unlink/`, null);
  }

  public getIncidentRecordingList(
    id: string,
    params?: ISnRecordingListQueryParameters,
  ): Observable<ISnListResponse<SnRecording>> {
    return this._httpClient.get(`/api/vaults/${id}/recordings/`, { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((incident) => new SnRecording(incident)),
      })),
    );
  }

  public unlinkRecording(incidentId: string, recordingId: string): Observable<null> {
    return this._httpClient.post<null>(
      `/api/vaults/${incidentId}/recordings/${recordingId}/unlink/`,
      null,
    );
  }

  public downloadIncidentFile(incident: SnIncident): Observable<any> {
    return this._httpClient
      .get(`/api/vaults/${incident.id}/download`, {
        responseType: 'blob',
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        switchMap((res: HttpEvent<any>) => {
          if (res.type === HttpEventType.Response) {
            if (res.body.type === 'application/zip') {
              return of(res);
            }
            return from(res.body?.text()).pipe(
              switchMap((response: any) => {
                const resObj = JSON.parse(response);
                if (resObj.async) {
                  return of(resObj);
                }
                return this._httpClient.get(resObj.location, {
                  responseType: 'blob',
                  reportProgress: true,
                  observe: 'events',
                });
              }),
            );
          }
          return of(res);
        }),
      );
  }
  public createIncident(
    data: Required<Pick<SnIncident, 'caseNumber' & 'description'>> & Partial<SnIncident>,
    showToast: boolean = true,
  ): Observable<SnIncident> {
    return this._httpClient.post('/api/vaults/', data).pipe(
      map((response: any) => new SnIncident(response)),
      tap({
        next: () => {
          if (showToast) {
            this._toastrService.success(t('vault.incidents.service.incidentCreated'));
          }
        },
        error: () => {
          if (showToast) {
            this._toastrService.error(t('vault.incidents.service.couldNotCreateIncident'));
          }
        },
      }),
    );
  }

  public createIncidentBulkRequest(data: any): Observable<any> {
    return this._httpClient.post('/api/vaults/', data).pipe(
      tap({
        next: () => this._toastrService.success(t('vault.incidents.service.incidentCreated')),
        error: () => this._toastrService.error(t('vault.incidents.service.couldNotCreateIncident')),
      }),
    );
  }

  public createVaultIncidentMedia(id: string, data: any, isFile: boolean = false): Observable<any> {
    const headers = new HttpHeaders({});
    if (!isFile) {
      headers.append('Content-Type', 'application/json');
    }
    return this._httpClient.post(`/api/vaults/${id}/media/`, data, { headers }).pipe(
      tap({
        next: () => this._toastrService.success(t('vault.incidents.service.mediaSentToVault')),
        error: () => this._toastrService.error(t('vault.incidents.service.couldNotSendMedia')),
      }),
    );
  }

  public getS3CredsAndUpload(id: string, file: File): Observable<any> {
    return this._httpClient
      .post(`/api/vaults/${id}/media/s3/`, {
        file: file.name,
        contentType: file.type,
        description: file.name,
      })
      .pipe(
        switchMap((resp) => {
          return this.uploadFile(resp, file);
        }),
        catchError((err) => {
          Object.keys(err.error).forEach((type) => {
            this._toastrService.error(err.error[type], file.name);
          });
          return throwError(err);
        }),
      );
  }

  public uploadFile(creds: any, file: File): Observable<any> {
    const formData = new FormData();
    Object.keys(creds.upload.fields).forEach((field) => {
      formData.append(field, creds.upload.fields[field]);
    });
    formData.append('file', file, file.name);
    return this._httpClient.post(creds.upload.url, formData);
  }

  public addLprToVault(vaultId: string, lprHitId: string): Observable<any> {
    const formData = {
      vaultId,
    };
    return this._httpClient.post(`/api/lpr/hits/${lprHitId}/add_to_vaults/`, formData).pipe(
      tap({
        next: () => this._toastrService.success(t('vault.incidents.service.lprAddedToVault')),
        error: () =>
          this._toastrService.error(t('vault.incidents.service.couldNotAddLprToVault')),
      }),
    );
  }

  public updateIncident(id: string, data: any): Observable<SnIncident> {
    return this._httpClient.patch(`/api/vaults/${id}/`, data).pipe(
      map((response: any) => new SnIncident(response)),
      tap({
        next: () => this._toastrService.success(t('vault.incidents.service.incidentUpdated')),
        error: () => this._toastrService.error(t('vault.incidents.service.couldNotUpdateIncident')),
      }),
    );
  }

  public deleteIncident(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('vault.incidents.service.deleteEvidenceConfirmation'),
        confirmButton: {
          text: 'DELETE',
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/vaults/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('vault.incidents.service.evidenceDeleted')),
          error: () =>
            this._toastrService.error(
              t('vault.incidents.service.evidenceCannotBeDeleted'),
            ),
        }),
      );
  }

  public createSecretCredential(data: any): Observable<any> {
    return this._httpClient.post('/api/vaults/secrets/', data).pipe(
      tap({
        next: () => this._toastrService.success(t('vault.incidents.service.secretCredentialCreated')),
        error: () =>
          this._toastrService.error(
            t('vault.incidents.service.unableToCreateSecretCredential'),
          ),
      }),
    );
  }

  public getSecretCredentialList(
    params: ISnRecordingListQueryParameters,
  ): Observable<ISnListResponse<FsSecretCredentials>> {
    return this._httpClient.get('/api/vaults/secrets/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results,
      })),
    );
  }

  public updateSecretCredential(id: string, data: any): Observable<FsSecretCredentials> {
    return this._httpClient.patch(`/api/vaults/secrets/${id}/`, data).pipe(
      map((credential) => new FsSecretCredentials(credential)),
      tap({
        next: () => this._toastrService.success(t('vault.incidents.service.secretCredentialUpdated')),
        error: () =>
          this._toastrService.error(
            t('vault.incidents.service.unableToUpdateSecretCredential'),
          ),
      }),
    );
  }

  public deleteSecretCredential(id: string): Observable<object> {
    return this._dialogService
      .confirm({
        message: t('vault.incidents.service.deleteSecretCredentialConfirmation'),
        confirmButton: {
          text: t('common.delete'),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/vaults/secrets/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('vault.incidents.service.secretCredentialDeleted')),
          error: () =>
            this._toastrService.error(t('vault.incidents.service.couldNotDeleteSecretCredential')),
        }),
      );
  }
}
